/** Типы API-сущностей, общие для нескольких страниц/сторов. */

export interface ClientDocument {
  id: string;
  documentableType: 'client' | 'user';
  documentableId: string;
  kind: 'offer' | 'pd_consent' | 'other';
  title: string | null;
  url: string;
  createdAt: string;
}

export interface Client {
  id: string;
  title: string;
  legalForm: 'ooo' | 'ip' | 'sz' | 'fl';
  /** Привязанный логин (role='client') — жёсткая связь */
  userId: string | null;
  enabled: boolean;
  deactivatedAt: string | null;
  primaryColor: string | null;
  logoUrl: string | null;
  phone: string | null;
  /** ПД клиента (clients.email) */
  email: string | null;
  inn: string | null;
  kpp: string | null;
  bik: string | null;
  checkingAccount: string | null;
  correspondentAccount: string | null;
  address: string | null;
  generalDirector: string | null;
  /** Конфиг полей сотрудника: {slug: 'required'|'optional'}; отсутствие slug = «скрыто» */
  workerFields: Record<string, 'required' | 'optional'> | null;
  /** Число исполнителей клиента — приходит в списке GET /admin/clients */
  workerCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface ClientWithDocuments extends Client {
  documents?: ClientDocument[];
}

/** Карточка «Логин» (user = логин + пароль): GET/PATCH …/:id/login */
export interface LoginInfo {
  id: string;
  login: string;
  enabled: boolean;
  lastLoginAt: string | null;
  deactivatedAt: string | null;
}

export interface Location {
  id: string;
  clientId: string;
  internalId: string;
  internalId2: string | null;
  name: string;
  enabled: boolean;
  deactivatedAt: string | null;
  city: string | null;
  region: string | null;
  province: string | null;
  district: string | null;
  address: string | null;
  url: string | null;
  type: string | null;
  description: string | null;
  phone: string | null;
  latitude: string | null;
  longitude: string | null;
  createdAt: string;
  updatedAt: string;
  client?: { id: string; title: string };
}

export interface WorkerFieldDefinition {
  slug: string;
  label: string;
  position: number;
  /** Поле — персональные данные (для будущей логики оферта/ОПД) */
  isPd: boolean;
}

export type TaskType = 'text_input' | 'photo' | 'checkbox' | 'action';

export interface TaskTemplate {
  id: string;
  visitTemplateId: string;
  slug: string;
  position: number;
  type: TaskType;
  title: string;
  instruction: string | null;
  aiPrompts: string[] | null;
  isRequired: boolean;
}

export interface TemplatePhoto {
  id: string;
  photoableType: 'visit_template' | 'task_template' | 'task';
  photoableId: string;
  url: string;
  position: number;
}

/** Жизненный цикл: new (несохранённый) → draft → запуск ⇄ пауза (правки — до запуска) */
export type TemplateStatus = 'new' | 'draft' | 'active' | 'paused';

export interface VisitTemplate {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  instruction: string | null;
  followUp: string | null;
  cost: string | null;
  aiContext: string | null;
  nextVisitTemplateId: string | null;
  clientId: string | null;
  status: TemplateStatus;
  /** #RRGGBB — акцентный цвет шаблона */
  color: string | null;
  isArchived: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface VisitTemplateDetail extends VisitTemplate {
  taskTemplates?: TaskTemplate[];
  task_templates?: TaskTemplate[];
  photos?: TemplatePhoto[];
  /** Число визитов за всю историю — для кнопки «Вернуть в черновик» */
  visits_count?: number;
}

/** Исполнитель: users (role='worker') + анкета из workers.
 *  Значения всех полей каталога (вкл. phone/email/гео) — в workerFields. */
export interface WorkerRow {
  id: string;
  login: string;
  name: string | null;
  clientId: string | null;
  internalId: string | null;
  supervisorId: string | null;
  workerFields: Record<string, string> | null;
  pdConsentAcceptedAt: string | null;
  offerAcceptedAt: string | null;
  enabled: boolean;
  deactivatedAt: string | null;
  createdAt: string;
  client?: { id: string; title: string } | null;
}

export const LEGAL_FORM_LABELS: Record<Client['legalForm'], string> = {
  ooo: 'ООО',
  ip: 'ИП',
  sz: 'СЗ',
  fl: 'ФЛ',
};

export const DOCUMENT_KIND_LABELS: Record<ClientDocument['kind'], string> = {
  offer: 'Оферта',
  pd_consent: 'Согласие на обработку ПД',
  other: 'Другое',
};

export const TASK_TYPE_LABELS: Record<TaskType, string> = {
  text_input: 'Текст',
  photo: 'Фото',
  checkbox: 'Галочка',
  action: 'Действие',
};
